/**
 * Prop place/erase pick the surface that is actually drawn. Elevation on hits
 * the raised cap; elevation off hits the flat waterline under the click.
 * Stored decoration x/y stay on the waterline so collision and migrate do not change.
 */

import { distanceSquared } from './document.ts';
import { elevatedBodiesInPaintOrder, type ElevatedPaintBody } from './elevationOrder.ts';
import { resolveElevationStack } from './elevationStack.ts';
import { plateauFromWaterline, pointInColumn, waterlineFromPlateau } from './extrude.ts';
import { pointInPolygon } from './islands.ts';
import { tessellateRing } from './ring.ts';
import { propHitsPoint } from './surfaces.ts';
import type { Island, MapDocument, Vec2 } from './types.ts';

export type PropPickOptions = {
  /** Match the raised column. False hits the flat waterline. Default true. */
  elevate?: boolean;
};

function pickElevation(elevation: number, options?: PropPickOptions): number {
  return options?.elevate === false ? 0 : elevation;
}

/** Top-most island whose drawn surface contains the click. A covering rock blocks. */
export function findIslandAtPlateau(
  map: MapDocument,
  point: Vec2,
  options?: PropPickOptions,
): Island | undefined {
  const bodies = elevatedBodiesInPaintOrder(map);
  const stack = resolveElevationStack(map);

  for (let i = bodies.length - 1; i >= 0; i -= 1) {
    const entry = bodies[i];

    if (!entry) {
      continue;
    }

    const world = stack.get(entry.body.id)?.worldElevation ?? entry.body.elevation;
    const waterline = waterlineFromPlateau(point, pickElevation(world, options));

    if (!pointInPolygon(waterline, tessellateRing(entry.body))) {
      continue;
    }

    if (entry.kind === 'rock') {
      return undefined;
    }

    return entry.body;
  }

  return undefined;
}

/**
 * Top-most painted island or rock under a screen point, including cliff walls.
 * Walks the same order the columns draw so a tall island covers a short rock.
 */
export function findElevatedBodyAt(
  map: MapDocument,
  point: Vec2,
): ElevatedPaintBody | undefined {
  const bodies = elevatedBodiesInPaintOrder(map);
  const stack = resolveElevationStack(map);

  for (let i = bodies.length - 1; i >= 0; i -= 1) {
    const entry = bodies[i];

    if (!entry) {
      continue;
    }

    const resolved = stack.get(entry.body.id);
    const world = resolved?.worldElevation ?? entry.body.elevation;

    if (pointInColumn(tessellateRing(entry.body), world, point, resolved?.baseElevation ?? 0)) {
      return entry;
    }
  }

  return undefined;
}

export function findDecorationAt(
  map: MapDocument,
  point: Vec2,
  radius: number,
  options?: PropPickOptions,
): { islandId: string; decorationId: string } | null {
  let best: { islandId: string; decorationId: string; dist: number } | null = null;
  const bodies = elevatedBodiesInPaintOrder(map);
  const stack = resolveElevationStack(map);

  for (let i = bodies.length - 1; i >= 0; i -= 1) {
    const entry = bodies[i];

    if (!entry || entry.kind !== 'island') {
      continue;
    }

    const island = entry.body;
    const world = stack.get(island.id)?.worldElevation ?? island.elevation;

    for (let d = island.decorations.length - 1; d >= 0; d -= 1) {
      const decoration = island.decorations[d];

      if (!decoration) {
        continue;
      }

      if (options?.elevate !== false && propOccludedByLand(map, island, decoration, true)) {
        continue;
      }

      const visual = plateauFromWaterline(decoration, pickElevation(world, options));

      if (!propHitsPoint(visual, decoration.catalog, decoration.scale, point, radius)) {
        continue;
      }

      const dist = distanceSquared(point, visual);

      if (!best || dist < best.dist) {
        best = { islandId: island.id, decorationId: decoration.id, dist };
      }
    }
  }

  return best ? { islandId: best.islandId, decorationId: best.decorationId } : null;
}

/**
 * A taller (or later same-height) column covers this prop’s base. The whole
 * sprite must hide — depth-sorting a 256 px image lets fronds leak around the
 * column. Elevation off never hides.
 */
export function propOccludedByLand(
  map: MapDocument,
  island: Island,
  decoration: Vec2,
  elevate = true,
): boolean {
  if (!elevate) {
    return false;
  }

  const stack = resolveElevationStack(map);
  const ownerWorld = stack.get(island.id)?.worldElevation ?? island.elevation;
  const visual = plateauFromWaterline(decoration, ownerWorld);
  const bodies = elevatedBodiesInPaintOrder(map);
  const ownerIndex = bodies.findIndex(
    (entry) => entry.kind === 'island' && entry.body.id === island.id,
  );

  for (let i = 0; i < bodies.length; i += 1) {
    const entry = bodies[i];

    if (!entry || (entry.kind === 'island' && entry.body.id === island.id)) {
      continue;
    }

    const other = stack.get(entry.body.id);
    const otherWorld = other?.worldElevation ?? entry.body.elevation;

    if (otherWorld < ownerWorld) {
      continue;
    }

    if (otherWorld === ownerWorld && i < ownerIndex) {
      continue;
    }

    const outline = tessellateRing(entry.body);

    if (
      pointInPolygon(decoration, outline) ||
      pointInColumn(outline, otherWorld, visual, other?.baseElevation ?? 0)
    ) {
      return true;
    }
  }

  return false;
}
